/**
 * Character appearance — the layered "paperdoll" identity that replaces the old
 * single `spriteId`. Stored on the Character document, synced via the Colyseus
 * `Appearance` schema, and carried in `CharacterDTO`. Each field is a small integer
 * id (a STYLE), some with a companion COLOUR id; see `manifest.ts` for valid ranges.
 *
 * This module is pure data + string helpers (no DOM): safe to import on both client
 * and server. The `/assets/...` URL builder lives client-side (see the sprite code);
 * here we only build the bare part FILENAME stem, which is the real shared invariant.
 */
import { C3D_EYES, C3D_HAIR, C3D_HAIR_COLORS, C3D_SKIN_TONES, C3D_TOP, C3D_BOTTOM, C3D_SHOES, C3D_HAT, C3D_MASK, C3D_BACK, C3D_GARMENT_COLORS, } from "./c3d.js";
/** The complete default look a brand-new character starts with (c3d base, round eyes, bald). */
export const DEFAULT_APPEARANCE = {
    head: 1,
    face: 0,
    skinTone: 0,
    hair: 0,
    hairColor: 0,
    backHair: 0,
    beard: 0,
    beardColor: 0,
    ears: 0,
    earsColor: 0,
    glasses: 0,
    glassesColor: 0,
    backAcc: 0,
    backAccColor: 0,
    top: 0,
    topColor: 0,
    bottom: 0,
    bottomColor: 0,
    shoes: 0,
    shoesColor: 0,
    hat: 0,
    hatColor: 0,
    mask: 0,
    maskColor: 0,
    weapon: 0,
};
/** Stable field order for hashing/serialisation — NEVER reorder (it's a cache key). */
const APPEARANCE_FIELDS = [
    "head", "face", "skinTone", "hair", "hairColor", "backHair", "beard", "beardColor",
    "ears", "earsColor", "glasses", "glassesColor", "backAcc", "backAccColor",
    "top", "bottom", "hat", "weapon", "topColor", "bottomColor", "hatColor",
    "shoes", "shoesColor",
    "mask", "maskColor", // appended (never reorder — the prefix is a cache key)
];
/**
 * A deterministic key for an appearance, for portrait/texture caches and DOM dataset
 * guards. Fixed field order — do not use `String(appearance)`/`JSON.stringify` (object
 * key order isn't guaranteed and `String` yields "[object Object]").
 */
export function appearanceHash(a) {
    return APPEARANCE_FIELDS.map((f) => a[f]).join(",");
}
/**
 * The bare part filename stem for a layer, e.g. `partFileName("hair", 3, 5)` -> "hair3_c5".
 * Colour `0` yields the base file (no `_c` suffix). The caller prefixes the asset URL.
 */
export function partFileName(folder, style, color) {
    return color > 0 ? `${folder}${style}_c${color}` : `${folder}${style}`;
}
/** A finite, in-range non-negative integer. */
function intIn(value, max) {
    return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= max ? value : null;
}
/**
 * Validate an untrusted appearance against the c3d catalog and return a FRESH,
 * clamped object containing only known fields — or `null` if any field is invalid.
 * Slot meanings in the c3d system: `face` = eye variant; `hair`/`hairColor` = c3d
 * hair style + colourway (style 0 = bald/none); everything else is reserved (0)
 * until its asset class exists.
 */
export function validateAppearance(raw) {
    if (typeof raw !== "object" || raw === null)
        return null;
    const r = raw;
    const face = intIn(r.face ?? 0, 99);
    if (face === null || !(face in C3D_EYES))
        return null;
    const hair = intIn(r.hair ?? 0, 99);
    if (hair === null || (hair !== 0 && !(hair in C3D_HAIR)))
        return null;
    const hairColor = intIn(r.hairColor ?? 0, C3D_HAIR_COLORS.length - 1) ?? 0;
    const head = intIn(r.head ?? 1, 1) ?? 1;
    const skinTone = intIn(r.skinTone ?? 0, C3D_SKIN_TONES.length - 1) ?? 0;
    // A garment slot: clamp style against its catalog (unknown → none) and colour
    // against the shared garment ramp. Style 0 forces colour 0.
    const garment = (styleKey, colorKey, catalog) => {
        let style = intIn(r[styleKey] ?? 0, 99) ?? 0;
        if (style !== 0 && !(style in catalog))
            style = 0;
        let color = intIn(r[colorKey] ?? 0, C3D_GARMENT_COLORS.length - 1) ?? 0;
        if (style === 0)
            color = 0;
        return { style, color };
    };
    const top = garment("top", "topColor", C3D_TOP);
    const bottom = garment("bottom", "bottomColor", C3D_BOTTOM);
    const shoes = garment("shoes", "shoesColor", C3D_SHOES);
    const hat = garment("hat", "hatColor", C3D_HAT);
    const mask = garment("mask", "maskColor", C3D_MASK);
    const backAcc = garment("backAcc", "backAccColor", C3D_BACK);
    return {
        ...DEFAULT_APPEARANCE,
        head,
        face,
        skinTone,
        hair,
        hairColor: hair === 0 ? 0 : hairColor,
        top: top.style, topColor: top.color,
        bottom: bottom.style, bottomColor: bottom.color,
        shoes: shoes.style, shoesColor: shoes.color,
        hat: hat.style, hatColor: hat.color,
        mask: mask.style, maskColor: mask.color,
        backAcc: backAcc.style, backAccColor: backAcc.color,
    };
}
//# sourceMappingURL=appearance.js.map